<script setup>
import { watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { Markdown } from 'tiptap-markdown'
import Placeholder from '@tiptap/extension-placeholder'
import RichTextToolbar from './RichTextToolbar.vue'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

const editor = useEditor({
  extensions: [
    StarterKit.configure({ link: { openOnClick: false } }),
    Image,
    Markdown.configure({ html: false, transformPastedText: true }),
    Placeholder.configure({ placeholder: () => t('richTextEditor.placeholder') }),
  ],
  onCreate({ editor }) {
    if (props.modelValue) {
      editor.chain()
        .command(({ tr }) => { tr.setMeta('addToHistory', false); return true })
        .setContent(props.modelValue)
        .run()
    }
  },
  onUpdate({ editor }) {
    emit('update:modelValue', editor.storage.markdown.getMarkdown())
  },
})

watch(
  () => props.modelValue,
  (val) => {
    if (!editor.value) return
    const current = editor.value.storage.markdown.getMarkdown()
    if (current === val) return
    editor.value.chain()
      .command(({ tr }) => { tr.setMeta('addToHistory', false); return true })
      .setContent(val)
      .run()
  },
)

onBeforeUnmount(() => editor.value?.destroy())
</script>

<template>
  <div class="border border-gray-600 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-cyan-500">
    <RichTextToolbar v-if="editor" :editor="editor" />
    <div
      class="crafter-markdown bg-gray-700 h-56 overflow-y-auto cursor-text resize-y min-h-28"
      @click="editor?.commands.focus()"
    >
      <EditorContent :editor="editor" />
    </div>
  </div>
</template>

<style scoped>
:deep(.tiptap p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}

:deep(.tiptap) {
  min-height: 100%;
  padding: 0.75rem 1rem;
  color: white;
  outline: none;
  cursor: text;
}

:deep(.tiptap img) {
  max-width: 100%;
  max-height: 175px;
  width: auto;
}
</style>
